import { Component, input, model, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { LoaderComponent } from '@examdojo/ui/loader';
import { NgxExtendedPdfViewerComponent, NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { tap } from 'rxjs';

@Component({
  selector: 'dojo-pdf-viewer',
  template: `
    <div class="bg-neutral-0 sticky right-0 top-0 flex items-center gap-1 border-b border-neutral-200 py-1.5 pl-3 pr-3">
      <div class="truncate text-xs text-neutral-400" [matTooltip]="name()">{{ name() }}</div>
      <div class="ml-auto flex items-center gap-1">
        <dojo-icon-button (click)="zoomIn()" [icon]="'magnifying-glass-plus'" />
        <dojo-icon-button (click)="zoomOut()" [icon]="'magnifying-glass-minus'" />
      </div>
    </div>
    <div class="h-full w-full">
      @if (!loaded()) {
        <div class="z-3 bg-neutral-0 absolute left-0 top-0 flex h-full w-full items-center justify-center">
          <dojo-loader />
        </div>
      }
      <ngx-extended-pdf-viewer
        #pdfViewer
        [src]="src()"
        [page]="page()"
        [zoom]="zoom()"
        [showToolbar]="false"
        [showSidebarButton]="false"
        [sidebarVisible]="false"
        [showFindButton]="false"
        [showPagingButtons]="false"
        [showZoomButtons]="false"
        [showPresentationModeButton]="false"
        [showOpenFileButton]="false"
        [showPrintButton]="false"
        [showSecondaryToolbarButton]="false"
        [showRotateButton]="false"
        [showHandToolButton]="false"
        [showSpreadButton]="false"
        [showPropertiesButton]="false"
        [showSignatureEditor]="false"
        [zoom]="zoom()"
        [ignoreKeyboard]="true"
        [textLayer]="false"
        (pdfLoaded)="onPdfLoaded()"
        (pageChange)="setPage($event)"
        (pdfLoadingFailed)="onPdfLoadingFailed($event)"
        [class]="'extended-pdf-viewer h-full w-full'"
      />
    </div>
  `,
  imports: [NgxExtendedPdfViewerModule, IconButtonComponent, MatTooltipModule, LoaderComponent],
  styles: [
    `
      .extended-pdf-viewer ::ng-deep {
        .body,
        #viewerContainer {
          background: theme('colors.neutral.50') !important;
        }

        .grab-to-pan-grab {
          top: 30px !important;
        }

        .pdfViewer .page {
          border: 0;
          box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.3);
          margin-top: 4px;
          margin-bottom: 9px;
        }
      }
    `,
  ],
  host: {
    class: 'flex flex-col h-full w-full relative',
  },
})
export class PdfViewerComponent {
  constructor() {
    toObservable(this.src)
      .pipe(
        tap(() => this.loaded.set(false)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly loaded = signal(false);
  readonly src = input.required<string>();
  readonly name = input.required<string>();
  readonly zoom = model<number>(50);
  readonly multiplier = 1.2;
  readonly pdfViewer = viewChild<NgxExtendedPdfViewerComponent>('pdfViewer');
  readonly page = model<number | undefined>(undefined);

  zoomIn() {
    this.zoom.update((zoom) => zoom * this.multiplier);
  }

  zoomOut() {
    this.zoom.update((zoom) => zoom / this.multiplier);
  }

  onPdfLoaded() {
    this.loaded.set(true);
  }

  onPdfLoadingFailed(event: unknown) {
    console.error('PDF loading failed:', event);
    this.loaded.set(false);
  }

  setPage(page: number) {
    this.page.set(page);
  }
}
